import React, { useState, useEffect } from "react";

import useEmblaCarousel from "embla-carousel-react";
import Lottie from "lottie-react";
import { Play, Info, X, Share2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import downArrowAnimation from "../assets/down_arrow.json";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import PremiumOverlay from "../components/PremiumOverlay";
import SmartAppBanner from "../components/SmartAppBanner";
import apiService from "../services/api";
import webPlayerHeartbeatService from "../services/webPlayerHeartbeat";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { isPremiumUser } from "../utils/premiumUtils";
import watchProgressUtils from "../utils/watchProgressUtils";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerMovieDetails = () => {
  const { mediaId } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [trailerPlaying, setTrailerPlaying] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [showProviders, setShowProviders] = useState(false);
  const [streamingProviders, setStreamingProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [showDescriptionOverlay, setShowDescriptionOverlay] = useState(false);

  // Get profile and playlist from storage
  const profile = JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist = JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  // Embla carousel for recommendations
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "keepSnaps",
    dragFree: true,
  });

  // Embla carousel for cast
  const [castEmblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 3,
    containScroll: "keepSnaps",
    dragFree: true,
  });

  // Embla carousel for collection
  const [collectionEmblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "keepSnaps",
    dragFree: true,
  });

  useEffect(() => {
    // Scroll to top when navigating to a new movie
    window.scrollTo(0, 0);

    async function fetchMovieDetails(id) {
      try {
        setLoading(true);
        let movieData = null;
        let tmdbData = null;
        const token = webPlayerStorage.getToken();

        // If user is logged in, try authenticated fetch first
        if (token) {
          try {
            const response = await apiService.webPlayerGetMovie(id);

            movieData = response?.data || response;
            // Check if we got valid data (not "Not Available")
            if (
              movieData?.name === "Not Available" ||
              movieData?.mediaId === -1
            ) {
              movieData = null;
            }
          } catch {
            // Auth fetch failed, will try other methods below
            movieData = null;
          }
        }

        // If no valid data yet, try NoAuth
        if (!movieData) {
          try {
            const noAuthResponse = await apiService.webPlayerRequest(
              `/v2/movies/${id}`,
              {
                method: "GET",
                headers: {
                  Authorization: "Bearer NoAuth",
                },
              }
            );
            const noAuthData = noAuthResponse?.data || noAuthResponse;

            // Check if NoAuth returned valid data
            if (
              noAuthData?.name &&
              noAuthData.name !== "Not Available" &&
              noAuthData?.mediaId !== -1
            ) {
              // If logged in, try to find movie in user's library by searching
              if (token && noAuthData?.tmdbId && noAuthData?.name) {
                try {
                  const searchResponse = await apiService.webPlayerRequest(
                    `/v2/movies?term=${encodeURIComponent(noAuthData.name)}&limit=20`,
                    { method: "GET" }
                  );
                  const searchResults = searchResponse?.data || [];
                  const userMovie = searchResults.find(
                    (m) => m.tmdbId === noAuthData.tmdbId
                  );

                  if (userMovie?.mediaId) {
                    const fullResponse = await apiService.webPlayerGetMovie(
                      userMovie.mediaId
                    );

                    movieData = fullResponse?.data || fullResponse;
                  }
                } catch {
                  // Search failed, use NoAuth data
                }
              }

              if (!movieData) {
                movieData = noAuthData;
              }
            }
          } catch {
            // NoAuth also failed
          }
        }

        // If still no data, try TMDB directly (ID might be a tmdbId from shared link)
        // Use Vercel endpoint since /api/tmdb doesn't exist on main server
        if (!movieData) {
          try {
            const tmdbResponse = await fetch(
              `https://web-player-lunovas-projects-076fd788.vercel.app/api/tmdb?id=${id}&type=movie`
            );

            if (tmdbResponse.ok) {
              tmdbData = await tmdbResponse.json();

              // If user is logged in, search their library by movie name
              if (token && tmdbData?.name) {
                try {
                  const searchResponse = await apiService.webPlayerRequest(
                    `/v2/movies?term=${encodeURIComponent(tmdbData.name)}&limit=20`,
                    { method: "GET" }
                  );
                  const searchResults = searchResponse?.data || [];

                  // Find movie with matching tmdbId
                  const userMovie = searchResults.find(
                    (m) =>
                      m.tmdbId === tmdbData.tmdbId ||
                      m.tmdbId === parseInt(id, 10)
                  );

                  if (userMovie?.mediaId) {
                    const fullResponse = await apiService.webPlayerGetMovie(
                      userMovie.mediaId
                    );
                    const userData = fullResponse?.data || fullResponse;

                    // Only use user data if it's valid (not "Not Available")
                    if (
                      userData?.name &&
                      userData.name !== "Not Available" &&
                      userData?.mediaId !== -1
                    ) {
                      movieData = userData;
                    }
                  }
                } catch {
                  // Search failed, use TMDB data as fallback
                }
              }

              // Use TMDB data if couldn't find valid data in user's library
              if (!movieData) {
                movieData = tmdbData;
              }
            }
          } catch {
            // TMDB fetch failed
          }
        }

        if (movieData) {
          setMovie(movieData);
          if (movieData.cast) {
            debugLog("Cast data for movie:", movieData.cast);
          }
          if (movieData.isBookmarked) {
            setIsBookmarked(true);
          }
        }
      } catch (error) {
        toast.error("Failed to load movie details");
      } finally {
        setLoading(false);
      }
    }

    fetchMovieDetails(mediaId);
    applyProgress();

    // Heartbeat ticks refresh the cache the API populates — re-read on each tick.
    const onHeartbeat = () => applyProgress();

    webPlayerHeartbeatService.addListener(onHeartbeat);

    return () => webPlayerHeartbeatService.removeListener(onHeartbeat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  // After closing the player, the cache has just been updated by
  // watchProgressUtils.savePlayerProgress() — re-read so the Resume button
  // and progress bar appear immediately without waiting for the next heartbeat.
  useEffect(() => {
    if (!playerOpen) {
      applyProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerOpen]);

  // Read progress from the same cache the player wrote it to — exactly what
  // Samsung's media-detail.js does (Storage.getWatchProgress(mediaId, 'movie')).
  // The cache is populated by:
  //   1. watchProgressUtils.saveProgress() — fires when the Plyr player closes
  //      after watching, so it's already up to date when the user lands here.
  //   2. /v2/heartbeat tick — server source-of-truth, refreshes every 5s.
  // Samsung threshold for "has progress": progress.progress > 60000 (ms).
  const applyProgress = () => {
    const p = watchProgressUtils.getValidatedProgress(String(mediaId), "movie");

    if (p && p.progress > 0) {
      setProgress(
        p.progressPercent ||
          (p.duration > 0 ? Math.round((p.progress / p.duration) * 100) : 0)
      );
    } else {
      setProgress(0);
    }
  };

  const fetchProgress = () => applyProgress();

  // Check if user is authenticated
  const isAuthenticated = !!webPlayerStorage.getToken();

  const handlePlay = () => {
    // If not authenticated, show "Where to Watch" overlay
    if (!isAuthenticated) {
      handleViewMore();

      return;
    }

    // Check premium status - only show overlay if logged in but not premium
    if (!isPremiumUser()) {
      setShowPremiumOverlay(true);

      return;
    }

    // Construct the stream URL for the movie
    let streamUrl = null;

    // Check if movie has a direct stream URL
    if (movie?.streamUrl) {
      try {
        // Try to decode if it's base64
        if (!movie.streamUrl.startsWith("http")) {
          streamUrl = atob(movie.streamUrl);
        } else {
          ({ streamUrl } = movie);
        }
      } catch (error) {
        console.error("Error decoding stream URL:", error);
        ({ streamUrl } = movie); // Use as-is if decode fails
      }
    } else if (movie?.id && playlist) {
      // Construct URL using playlist data
      if (playlist.url && playlist.username && playlist.password) {
        const baseUrl = playlist.url.replace(/\/$/, "");

        // For movies, use the movie endpoint with extension
        streamUrl = `${baseUrl}/movie/${playlist.username}/${playlist.password}/${movie.id}`;
      }
    }

    if (streamUrl) {
      setCurrentStreamUrl(streamUrl);
      setPlayerOpen(true);
    } else {
      toast.error("Stream not available");
    }
  };

  const toggleBookmark = async () => {
    try {
      if (!isBookmarked) {
        await apiService.webPlayerCreateBookmark(
          movie.mediaId,
          movie.mediaType
        );
      } else {
        await apiService.webPlayerDeleteBookmark(
          movie.mediaId,
          movie.mediaType
        );
      }

      setIsBookmarked(!isBookmarked);
      toast.success(
        isBookmarked ? "Removed from favorites" : "Added to favorites"
      );
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleViewMore = async () => {
    setShowProviders(true);
    setLoadingProviders(true);

    try {
      // Fetch streaming providers from API
      const result = await apiService.webPlayerRequest(
        `/v2/movies/${mediaId}`,
        {
          method: "GET",
        }
      );

      if (result?.streamingProviders) {
        setStreamingProviders(result.streamingProviders);
      } else if (result?.data?.streamingProviders) {
        setStreamingProviders(result.data.streamingProviders);
      }
    } catch (error) {
      console.error("Error fetching streaming providers:", error);
      toast.error("Failed to load streaming providers");
    } finally {
      setLoadingProviders(false);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return "";
    const parts = duration.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    return `${hours}h ${minutes}m`;
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&\n?#]+)/
    );

    return match ? match[1] : null;
  };

  const handleShare = async () => {
    // Build share URL using tmdbId for OG tags (crawlers fetch from TMDB)
    const shareUrl = movie?.tmdbId
      ? `${window.location.origin}/web-player/movie/${movie.tmdbId}`
      : `${window.location.origin}/web-player/movie/${mediaId}`;
    const shareTitle = movie?.name || "Check out this movie";
    const shareText = movie?.description
      ? `${shareTitle} - ${movie.description.slice(0, 100)}...`
      : shareTitle;

    // Try native share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== "AbortError") {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      // Fall back to clipboard copy
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");

      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Movie not found</div>
      </div>
    );
  }

  return (
    <>
      {/* Dynamic Meta Tags for Sharing */}
      <Helmet>
        <title>
          {movie?.name
            ? `${movie.name} (${movie.year || ""}) - Lunova`
            : "Movie - Lunova"}
        </title>
        <meta
          content={movie?.description || "Watch this movie on Lunova"}
          name="description"
        />

        {/* Open Graph */}
        <meta content="video.movie" property="og:type" />
        <meta
          content={
            movie?.name ? `${movie.name} (${movie.year || ""})` : "Movie"
          }
          property="og:title"
        />
        <meta
          content={movie?.description || "Watch this movie on Lunova"}
          property="og:description"
        />
        <meta
          content={movie?.backdropPath || movie?.posterPath || "/webplayer.jpg"}
          property="og:image"
        />
        <meta
          content={`https://play.lunova.app/web-player/movie/${mediaId}`}
          property="og:url"
        />
        <meta content="Lunova" property="og:site_name" />

        {/* Twitter Card */}
        <meta content="summary_large_image" name="twitter:card" />
        <meta
          content={
            movie?.name ? `${movie.name} (${movie.year || ""})` : "Movie"
          }
          name="twitter:title"
        />
        <meta
          content={movie?.description || "Watch this movie on Lunova"}
          name="twitter:description"
        />
        <meta
          content={movie?.backdropPath || movie?.posterPath || "/webplayer.jpg"}
          name="twitter:image"
        />

        {/* App Links */}
        <meta
          content="app-id=6504567890, app-argument=lunova://movie/{mediaId}"
          name="apple-itunes-app"
        />
      </Helmet>

      <SmartAppBanner />

      {/* Mobile Menu */}
      <MobileMenu />

      <div className="min-h-screen text-white relative">
        {/* Fixed gradient background for entire page */}
        <div className="fixed inset-0 bg-gradient-to-b from-gray-600 via-gray-800 to-black z-0" />

        {/* Dashboard Button - Bottom Right */}
        <button
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
          onClick={() => {
            // Clear all session data
            webPlayerStorage.clearAll();
            // Redirect to dashboard
            window.location.href = "https://dashboard.lunova.app";
          }}
        >
          Dashboard
        </button>

        {/* Left Menu */}
        <LeftMenu
          activeMenuItem="movies"
          playlist={playlist}
          profile={profile}
        />

        {/* Backdrop Hero Section - Full viewport height */}
        <div className="relative h-screen z-10">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${movie.backdropPath})`,
            }}
          >
            {/* Gradient Overlays - Lighter for better image visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
          </div>

          {/* Movie Info - Positioned at bottom of viewport */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16 z-20">
            <div className="max-w-7xl mx-auto">
              {/* Title and Logo */}
              <div className="mb-6">
                {movie.logoPath ? (
                  <img
                    alt={movie.name}
                    className="h-20 lg:h-32 object-contain mb-4"
                    src={movie.logoPath}
                  />
                ) : (
                  <h1 className="text-4xl lg:text-6xl font-bold mb-4">
                    {movie.name}
                  </h1>
                )}
              </div>

              <div className="flex gap-4 mb-3">
                {/* Rating Badge */}
                <div className="inline-flex items-center justify-center border border-gray-400 px-3 py-2 rounded text-white text-base font-bold min-h-[40px]">
                  {movie.rating || "NR"}
                </div>

                {/* Two-line content next to rating */}
                <div className="flex flex-col gap-2">
                  {/* Line 1: Genre, Year, Type */}
                  <div className="flex items-center gap-3 text-white/90 text-base">
                    <span>{movie.genres}</span>
                    <span className="text-white/50">•</span>
                    <span>{movie.year}</span>
                    <span className="text-white/50">•</span>
                    <span>Movie</span>
                  </div>

                  {/* Line 2: Watch Time */}
                  {movie.duration && (
                    <div className="flex items-center gap-3">
                      <span className="text-white/90 text-base whitespace-nowrap">
                        Watch Time: {formatDuration(movie.duration)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rotten Tomatoes Ratings - Line 3 */}
              {(movie.rottenTomatoes?.critics?.score ||
                movie.rottenTomatoes?.audience?.score) && (
                <div className="flex items-center gap-4 mb-6">
                  {movie.rottenTomatoes?.critics?.score && (
                    <div className="flex items-center gap-2">
                      <img
                        alt="Critics"
                        className="w-5 h-5"
                        src={movie.rottenTomatoes.critics.icon}
                      />
                      <span className="font-semibold text-white">
                        {movie.rottenTomatoes.critics.score}%
                      </span>
                    </div>
                  )}
                  {movie.rottenTomatoes?.audience?.score && (
                    <div className="flex items-center gap-2">
                      <img
                        alt="Audience"
                        className="w-5 h-5"
                        src={movie.rottenTomatoes.audience.icon}
                      />
                      <span className="font-semibold text-white">
                        {movie.rottenTomatoes.audience.score}%
                      </span>
                    </div>
                  )}
                  {movie.rottenTomatoes?.reviews && (
                    <span className="text-sm text-gray-400">
                      {movie.rottenTomatoes.reviews}
                    </span>
                  )}
                </div>
              )}

              {/* Description */}
              {movie.description && (
                <p
                  aria-label="Read full description"
                  className="text-gray-300 max-w-3xl mb-8 line-clamp-3 cursor-pointer hover:text-white transition-colors"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Check if text is truncated (has ellipsis)
                    if (
                      e.currentTarget.scrollHeight >
                      e.currentTarget.clientHeight
                    ) {
                      setShowDescriptionOverlay(true);
                    }
                  }}
                  onKeyDown={activateOnEnterOrSpace}
                >
                  {movie.description}
                </p>
              )}

              {/* Progress Bar - Show if there's progress */}
              {progress > 0 && progress < 95 && (
                <div className="max-w-md mb-4">
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-1">
                    <span>Continue Watching</span>
                    <span>{progress}% complete</span>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 flex-wrap">
                {isAuthenticated ? (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors"
                    onClick={handlePlay}
                  >
                    <Play className="w-5 h-5 fill-current" />
                    {progress > 0 && progress < 95 ? "Resume" : "Play"}
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors"
                    onClick={handleViewMore}
                  >
                    <Info className="w-5 h-5" />
                    Where to Watch
                  </button>
                )}

                {movie.trailer && (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                    onClick={() => setTrailerPlaying(true)}
                  >
                    <Play className="w-5 h-5" />
                    Trailer
                  </button>
                )}

                {isAuthenticated && (
                  <>
                    <button
                      className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                      onClick={toggleBookmark}
                    >
                      {isBookmarked ? (
                        <>
                          <i className="fas fa-bookmark text-yellow-500 text-lg" />
                          <span className="text-yellow-500">In Favorites</span>
                        </>
                      ) : (
                        <>
                          <i className="far fa-bookmark text-white text-lg" />
                          <span>Favorites</span>
                        </>
                      )}
                    </button>

                    <button
                      className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                      onClick={handleViewMore}
                    >
                      <Info className="w-5 h-5" />
                      View More
                    </button>
                  </>
                )}

                <button
                  className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                  onClick={handleShare}
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Down Arrow Animation - Bottom Right */}
          <div className="absolute bottom-8 right-8 z-30">
            <Lottie
              animationData={downArrowAnimation}
              autoplay={true}
              loop={true}
              style={{ width: 60, height: 60 }}
            />
          </div>
        </div>

        {/* Cast Section */}
        {movie.cast && movie.cast.length > 0 && (
          <div className="relative px-8 lg:px-16 py-12 z-10 overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-semibold mb-6">Cast & Characters</h2>
            </div>
            <div className="max-w-7xl mx-auto">
              <div
                className="overflow-hidden select-none -mr-8 lg:-mr-16"
                ref={castEmblaRef}
              >
                <div
                  className="flex gap-4"
                  style={{ padding: "10px 10px 10px 10px" }}
                >
                  {movie.cast.map((actor, index) => {
                    const castId =
                      actor.id ||
                      actor.castId ||
                      actor.tmdbId ||
                      actor.personId ||
                      actor.person_id ||
                      actor.credit_id ||
                      actor.cast_id;

                    return (
                      <div
                        className="flex-shrink-0 w-32 text-center cursor-pointer group"
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          debugLog(
                            "Cast click - actor:",
                            actor,
                            "castId:",
                            castId
                          );
                          if (castId) {
                            navigate(`/web-player/cast/${castId}`);
                          } else {
                            console.warn("No castId found for actor:", actor);
                          }
                        }}
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        <div className="w-32 h-32 rounded-full overflow-hidden mb-2 mx-auto border-4 border-transparent group-hover:border-white group-hover:scale-110 transition-all duration-200">
                          {actor.profilePath ? (
                            <img
                              alt={actor.name}
                              className="w-full h-full object-cover"
                              draggable="false"
                              src={actor.profilePath}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-700">
                              <svg
                                className="w-12 h-12 text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  clipRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  fillRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate text-white">
                          {actor.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {actor.character}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Collection Section */}
        {movie.collection &&
          movie.collection.parts &&
          movie.collection.parts.length > 0 && (
            <div className="relative px-8 lg:px-16 py-12 z-10">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-2xl font-semibold mb-6">
                  {movie.collection.name}
                </h2>
              </div>

              {/* Collection Carousel with Embla */}
              <div className="max-w-7xl mx-auto">
                <div
                  className="overflow-visible select-none -mr-8 lg:-mr-16"
                  ref={collectionEmblaRef}
                  style={{ padding: "20px 10px" }}
                >
                  <div className="flex gap-4">
                    {movie.collection.parts.map((item) => (
                      <div
                        className="cursor-pointer flex-shrink-0 w-[calc(100%/2.5)] sm:w-[calc(100%/4)] lg:w-[calc(100%/6.5)] max-[932px]:landscape:w-[calc(100%/4.5)]"
                        key={item.mediaId || item.tmdbId}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate(`/web-player/movie/${item.mediaId}`)
                        }
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        <article className="transform-gpu">
                          <div className="relative group">
                            <div
                              className="lunova-card relative aspect-[2/3] card-radius-poster bg-gray-800"
                              style={{
                                transformOrigin: "center center",
                                boxShadow:
                                  "0 15px 35px -10px rgba(0,0,0,0.75), " +
                                  "0 10px 20px -5px rgba(0,0,0,0.5)",
                              }}
                            >
                              <div className="lunova-card-clip">
                                <img
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                  draggable={false}
                                  src={item.posterPath}
                                  onDragStart={(e) => e.preventDefault()}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement.innerHTML = `<div class="flex items-center justify-center text-white text-sm font-bold p-4 text-center h-full" style="background: linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)">${
                                      item.name || "Content"
                                    }</div>`;
                                  }}
                                />
                              </div>
                            </div>

                            {/* Rotten Tomatoes Ratings */}
                            {(item.rottenTomatoes?.critics?.score ||
                              item.rottenTomatoes?.audience?.score) && (
                              <div className="flex justify-center gap-3 mt-2">
                                {item.rottenTomatoes?.critics?.score && (
                                  <div className="flex items-center gap-1">
                                    <img
                                      alt="Critics"
                                      className="w-4 h-4"
                                      src={item.rottenTomatoes.critics.icon}
                                    />
                                    <span className="text-sm text-white font-semibold">
                                      {item.rottenTomatoes.critics.score}%
                                    </span>
                                  </div>
                                )}
                                {item.rottenTomatoes?.audience?.score && (
                                  <div className="flex items-center gap-1">
                                    <img
                                      alt="Audience"
                                      className="w-4 h-4"
                                      src={item.rottenTomatoes.audience.icon}
                                    />
                                    <span className="text-sm text-white font-semibold">
                                      {item.rottenTomatoes.audience.score}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </article>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Recommendations Section */}
        {movie.recommendations && movie.recommendations.length > 0 && (
          <div className="relative px-8 lg:px-16 py-12 pb-20 z-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-semibold mb-6">More Like This</h2>
            </div>

            {/* Recommendations Carousel with Embla */}
            <div className="max-w-7xl mx-auto">
              <div
                className="overflow-visible select-none -mr-8 lg:-mr-16"
                ref={emblaRef}
                style={{ padding: "20px 10px" }}
              >
                <div className="flex gap-4">
                  {movie.recommendations.map((item) => (
                    <div
                      className="cursor-pointer flex-shrink-0 w-[calc(100%/2.5)] sm:w-[calc(100%/4)] lg:w-[calc(100%/6.5)] max-[932px]:landscape:w-[calc(100%/4.5)]"
                      key={item.mediaId}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/web-player/movie/${item.mediaId}`)
                      }
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      <article className="transform-gpu">
                        <div className="relative group">
                          <div
                            className="lunova-card relative aspect-[2/3] card-radius-poster bg-gray-800"
                            style={{
                              transformOrigin: "center center",
                              boxShadow:
                                "0 15px 35px -10px rgba(0,0,0,0.75), " +
                                "0 10px 20px -5px rgba(0,0,0,0.5)",
                            }}
                          >
                            <div className="lunova-card-clip">
                              <img
                                alt={item.name}
                                className="w-full h-full object-cover"
                                draggable={false}
                                src={item.posterPath}
                                onDragStart={(e) => e.preventDefault()}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.parentElement.innerHTML = `<div class="flex items-center justify-center text-white text-sm font-bold p-4 text-center h-full" style="background: linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)">${
                                    item.name || "Content"
                                  }</div>`;
                                }}
                              />
                            </div>
                          </div>

                          {/* Rotten Tomatoes Ratings - Centered below the card */}
                          {(item.rottenTomatoes?.critics?.score ||
                            item.rottenTomatoes?.audience?.score) && (
                            <div className="flex justify-center gap-3 mt-2">
                              {item.rottenTomatoes?.critics?.score && (
                                <div className="flex items-center gap-1">
                                  <img
                                    alt="Critics"
                                    className="w-4 h-4"
                                    src={item.rottenTomatoes.critics.icon}
                                  />
                                  <span className="text-sm text-white font-semibold">
                                    {item.rottenTomatoes.critics.score}%
                                  </span>
                                </div>
                              )}
                              {item.rottenTomatoes?.audience?.score && (
                                <div className="flex items-center gap-1">
                                  <img
                                    alt="Audience"
                                    className="w-4 h-4"
                                    src={item.rottenTomatoes.audience.icon}
                                  />
                                  <span className="text-sm text-white font-semibold">
                                    {item.rottenTomatoes.audience.score}%
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trailer Modal */}
        {trailerPlaying && movie.trailer && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setTrailerPlaying(false)}
          >
            <div
              className="relative w-full max-w-5xl aspect-video"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute -top-12 right-0 text-white hover:text-gray-300"
                onClick={() => setTrailerPlaying(false)}
              >
                <X className="w-8 h-8" />
              </button>
              <iframe
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                className="w-full h-full rounded-lg"
                frameBorder="0"
                src={`https://www.youtube.com/embed/${extractYouTubeId(movie.trailer)}?autoplay=1`}
                title="Trailer"
              />
            </div>
          </div>
        )}

        {/* Description Overlay */}
        {showDescriptionOverlay && movie.description && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setShowDescriptionOverlay(false)}
          >
            <div
              className="relative w-full max-w-2xl bg-gray-900 rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-300"
                onClick={() => setShowDescriptionOverlay(false)}
              >
                <X className="w-6 h-6" />
              </button>
              <h3 className="text-xl font-bold text-white mb-4">
                {movie.name || movie.title}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {movie.description}
              </p>
            </div>
          </div>
        )}

        <style>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {/* Video Player Modal */}
        {playerOpen && (
          <PlyrVideoPlayer
            backdropPath={movie?.backdropPath}
            isOpen={playerOpen}
            mediaId={movie?.mediaId || movie?.id}
            mediaType="movie"
            posterPath={movie?.posterPath}
            streamUrl={currentStreamUrl}
            title={movie?.name || movie?.title}
            onClose={() => {
              setPlayerOpen(false);
              setCurrentStreamUrl(null);
              // Refresh progress after closing player
              fetchProgress();
            }}
          />
        )}

        {/* Where to Watch Modal */}
        {showProviders && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setShowProviders(false)}
          >
            <div
              className="relative w-full max-w-4xl bg-gray-900 rounded-lg overflow-hidden"
              role="presentation"
              style={{
                backgroundImage: 'url("/empty_seats.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/80" />

              {/* Content */}
              <div className="relative z-10 p-8">
                <button
                  className="absolute top-4 right-4 text-white hover:text-gray-300"
                  onClick={() => setShowProviders(false)}
                >
                  <X className="w-8 h-8" />
                </button>

                <h2 className="text-3xl font-bold text-white mb-2 text-center">
                  Where to watch
                </h2>
                <p className="text-xl text-gray-300 mb-8 text-center">
                  {movie.name}
                </p>

                {/* Loading State */}
                {loadingProviders ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500" />
                  </div>
                ) : (
                  <>
                    {/* Streaming Providers Grid */}
                    <div className="flex flex-wrap justify-center gap-6 mb-8">
                      {streamingProviders && streamingProviders.length > 0 ? (
                        streamingProviders.map((provider, index) => (
                          <a
                            className="group relative transition-all hover:scale-110 cursor-pointer"
                            href={
                              provider.deepLink ||
                              provider.link ||
                              provider.url ||
                              "#"
                            }
                            key={index}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            <div className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center">
                              {provider.logoPath || provider.logo ? (
                                <img
                                  alt={
                                    provider.provider ||
                                    provider.name ||
                                    provider.providerName
                                  }
                                  className="w-full h-full object-cover"
                                  src={provider.logoPath || provider.logo}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-700 rounded-full">
                                  <span className="text-white font-semibold text-sm text-center px-2">
                                    {provider.provider ||
                                      provider.name ||
                                      provider.providerName}
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-center text-gray-300 mt-2 max-w-[96px] mx-auto truncate">
                              {provider.provider ||
                                provider.name ||
                                provider.providerName}
                            </p>
                          </a>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-400 text-lg">
                            No streaming providers available
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Common Providers if no specific ones */}
                    {(!streamingProviders ||
                      streamingProviders.length === 0) && (
                      <div className="flex flex-wrap justify-center gap-6 mb-8">
                        {[
                          "AMC+",
                          "Fandango",
                          "Netflix",
                          "Prime Video",
                          "Hulu",
                          "Disney+",
                          "Apple TV+",
                          "Peacock",
                        ].map((provider) => (
                          <div
                            className="group relative transition-all hover:scale-110 cursor-pointer"
                            key={provider}
                          >
                            <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm text-center px-2">
                                {provider}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Tip */}
                <div className="text-center text-gray-400 text-sm">
                  <p>Tip: Tap any provider's logo to open its website</p>
                  <p>
                    Check each service for current subscription deals and trial
                    offers
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Premium Overlay */}
        <PremiumOverlay
          feature="movie"
          isOpen={showPremiumOverlay}
          onClose={() => setShowPremiumOverlay(false)}
        />
      </div>
    </>
  );
};

export default WebPlayerMovieDetails;
